import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { AppPageHeader } from '@/components/layout/AppPage';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Brain, TrendingUp, FileText, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREA_COLORS = ['#2ecc5a', '#1a8c38', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#ef4444'];

const PANELS = [
  { id: 'practice', label: 'Practice Areas' },
  { id: 'documents', label: 'Document Mix' },
  { id: 'courts', label: 'Courts & Forums' },
  { id: 'activity', label: 'Activity Charts' },
  { id: 'timeline', label: 'Research Timeline' },
];

function ExpandPanel({ id, label, open, onToggle, children }) {
  return (
    <div style={{
      borderRadius: '18px',
      border: '1px solid var(--border-default)',
      background: 'var(--bg-elevated)',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-ui)',
        }}
      >
        {label}
        {open ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-subtle)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function DataRows({ items, emptyText }) {
  if (!items.length) return <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>{emptyText}</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
      {items.map(item => (
        <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', borderRadius: '10px', background: 'var(--bg-surface)', fontSize: '12px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>{item.name}</span>
          <strong style={{ color: 'var(--text-primary)' }}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

export default function KnowledgePage() {
  const { user, getAuthHeaders } = useAuth();
  const [kg, setKg] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openPanel, setOpenPanel] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [kgResp, statsResp] = await Promise.all([
        fetch(`${API}/knowledge`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/user/stats`, { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      if (kgResp.ok) setKg(await kgResp.json());
      if (statsResp.ok) setStats(await statsResp.json());
    } catch {}
    finally { setIsLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const practiceData = kg?.practice_areas ? Object.entries(kg.practice_areas).map(([name, value]) => ({ name, value })) : [];
  const score = kg?.personalisation_score || 0;
  const docTypeData = kg?.document_types ? Object.entries(kg.document_types).map(([name, value]) => ({ name, value })) : [];
  const courtData = kg?.court_usage ? Object.entries(kg.court_usage).map(([name, value]) => ({ name, value })) : [];
  const scoreBreakdown = kg?.score_breakdown ? Object.entries(kg.score_breakdown) : [];
  const profileRole = kg?.user_profile?.role || user?.role || 'Unknown';
  const profileInstitution = kg?.user_profile?.institution || user?.institution || '';

  const toggle = (id) => setOpenPanel(prev => prev === id ? null : id);

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="knowledge-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AppPageHeader
          eyebrow="Profile Insights"
          title="How Advoq is learning your practice"
        />

        {/* Fixed overview strip — not scrollable */}
        <div style={{
          flexShrink: 0,
          padding: '16px 20px',
          borderBottom: '1px solid color-mix(in srgb, var(--border-default) 40%, transparent)',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr 1fr 1fr',
          gap: '14px',
          alignItems: 'center',
        }}>
          {/* Score ring */}
          <div style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
            {isLoading ? (
              <Skeleton style={{ width: '72px', height: '72px', borderRadius: '50%' }} />
            ) : (
              <>
                <svg style={{ width: '72px', height: '72px', transform: 'rotate(-90deg)' }} viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="var(--border-subtle)" strokeWidth="10" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke="var(--accent-primary)" strokeWidth="10"
                    strokeDasharray={`${score * 2.64} 264`} strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-primary)' }}>{score}</span>
                </div>
              </>
            )}
          </div>

          {/* Quick stats */}
          {[
            { label: 'Chats', value: stats?.total_chats || 0, Icon: MessageSquare },
            { label: 'Documents', value: stats?.total_documents || 0, Icon: FileText },
            { label: 'Messages', value: stats?.total_messages || 0, Icon: TrendingUp },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {isLoading
                ? <Skeleton style={{ width: '48px', height: '24px', borderRadius: '6px' }} />
                : <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{s.value}</span>
              }
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Profile identity row */}
        <div style={{
          flexShrink: 0,
          padding: '10px 20px',
          borderBottom: '1px solid color-mix(in srgb, var(--border-default) 30%, transparent)',
          display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Brain size={13} style={{ color: 'var(--accent-primary)' }} />
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{profileRole}</strong>
              {profileInstitution ? ` · ${profileInstitution}` : ''}
            </span>
          </div>
          {scoreBreakdown.length > 0 && scoreBreakdown.map(([label, value]) => (
            <span key={label} style={{
              fontSize: '11px', padding: '3px 8px', borderRadius: '999px',
              background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600,
            }}>
              {label}: {value}
            </span>
          ))}
        </div>

        {/* Expandable panels — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <ExpandPanel id="practice" label="Practice Areas" open={openPanel === 'practice'} onToggle={() => toggle('practice')}>
            {practiceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180} style={{ marginTop: '12px' }}>
                <PieChart>
                  <Pie data={practiceData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {practiceData.map((_, i) => <Cell key={i} fill={AREA_COLORS[i % AREA_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', color: 'var(--text-primary)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>Start chatting to build your practice profile.</p>}
          </ExpandPanel>

          <ExpandPanel id="documents" label="Document Mix" open={openPanel === 'documents'} onToggle={() => toggle('documents')}>
            <DataRows items={docTypeData.slice(0, 6)} emptyText="Upload or draft documents to teach Advoq your work product style." />
          </ExpandPanel>

          <ExpandPanel id="courts" label="Courts & Forums" open={openPanel === 'courts'} onToggle={() => toggle('courts')}>
            <DataRows items={courtData.slice(0, 6)} emptyText="Preferred courts will accumulate here as you work." />
          </ExpandPanel>

          <ExpandPanel id="activity" label="Activity Charts" open={openPanel === 'activity'} onToggle={() => toggle('activity')}>
            {practiceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180} style={{ marginTop: '12px' }}>
                <BarChart data={practiceData}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                  <Bar dataKey="value" fill="var(--accent-primary)" radius={[4, 4, 0, 0]} />
                  <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', color: 'var(--text-primary)' }} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>No activity data yet.</p>}
          </ExpandPanel>

          <ExpandPanel id="timeline" label="Research Timeline" open={openPanel === 'timeline'} onToggle={() => toggle('timeline')}>
            {kg?.topics_researched?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
                {kg.topics_researched.slice(-10).reverse().map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderRadius: '10px', background: 'var(--bg-surface)' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-primary)', flexShrink: 0 }} />
                    <p style={{ flex: 1, margin: 0, fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.topic}</p>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{t.mode}</span>
                  </div>
                ))}
              </div>
            ) : <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>No research topics yet. Start chatting to build your timeline.</p>}
          </ExpandPanel>
        </div>
      </main>
    </div>
  );
}
